import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";

interface MCheckboxGroupProps {
  // Note that values are set in useEffect. If you're going to pass a func to get the options,
  // be sure to memoize it so it doesn't get called every render and reset the selections
  fields: MCheckboxGroupOption[]; 
  cellSize?: number;
  containerClassName?: string;
  gridClassname?: string;
  controlMethods?: (methods: MCheckboxGroupControlMethods) => void;
  onChange?: (value: string, checked: boolean) => void;
}

export interface MCheckboxGroupOption {
  value: string; // this is meant to be a code to identify the field, it isnt the value of if its checked or not
  label: string | React.ReactNode;
  defaultChecked?: boolean;
  disabled?: boolean;
  checked?: boolean; // used for controlled mode - parent owns the state
}

export interface MCheckboxGroupControlMethods {
  getValues: () => MCheckboxValue[];  // Get all checkbox values
  getChecked: () => string[]; // Get selected values
  getUnChecked: () => string[]; // Get unselected values
  checkAll: () => void; // Select all (uncontrolled mode only)
  unCheckAll: () => void; // Unselect all (uncontrolled mode only)
}

export interface MCheckboxValue {
  value: string;
  checked: boolean;
}

const MCheckboxGroup = (props: MCheckboxGroupProps) => {
  // Controlled mode: parent owns state via `checked` prop
  // Uncontrolled mode: component owns state, initialized from `defaultChecked`
  const isControlled = props.fields.some(f => f.checked !== undefined);

  const [checkedMap, setCheckedMap] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(props.fields.map(f => [f.value, !!f.defaultChecked]))
  );

  const getCheckedState = (index: number): boolean =>
    isControlled ? !!props.fields[index].checked : !!checkedMap[props.fields[index].value];

  // When fields change, add entries for any new fields without disturbing existing state
  useEffect(() => {
    if (!isControlled) {
      setCheckedMap(prev => {
        const updated = { ...prev };
        let changed = false;
        for (const f of props.fields) {
          if (!(f.value in updated)) {
            updated[f.value] = !!f.defaultChecked;
            changed = true;
          }
        }
        return changed ? updated : prev;
      });
    }
  }, [props.fields, isControlled]);

  useEffect(() => {
    if (props.controlMethods) {
      props.controlMethods({
        getValues: () => props.fields.map((f, i) => ({ value: f.value, checked: getCheckedState(i) })),
        getChecked: () => props.fields.filter((_, i) => getCheckedState(i)).map(f => f.value),
        getUnChecked: () => props.fields.filter((_, i) => !getCheckedState(i)).map(f => f.value),
        checkAll: () => {
          if (!isControlled) setCheckedMap(Object.fromEntries(props.fields.map(f => [f.value, true])));
        },
        unCheckAll: () => {
          if (!isControlled) setCheckedMap(Object.fromEntries(props.fields.map(f => [f.value, false])));
        },
      });
    }
  });

  const onChange = (index: number) => {
    const field = props.fields[index];
    if (isControlled) {
      // Controlled: just notify parent, don't update internal state
      props.onChange?.(field.value, !field.checked);
    } else {
      // Uncontrolled: update internal state
      const newChecked = !checkedMap[field.value];
      setCheckedMap(prev => ({ ...prev, [field.value]: newChecked }));
      props.onChange?.(field.value, newChecked);
    }
  };

  // Calculate width based on cellSize (assuming 12-column grid)
  const getItemWidth = () => {
    if (!props.cellSize) return "auto";
    const percentage = (props.cellSize / 12) * 100;
    return `${percentage}%`;
  };

  return (
    <Box 
      className={props.gridClassname || ''}
      sx={{ 
        display: "flex", 
        flexWrap: "wrap", 
        gap: 1,
        padding: 1,
      }}
    >
      {props.fields.map(({ label, value, disabled }, index) =>
        <Box 
          key={`${value}-${index}`} 
          sx={{ 
            width: getItemWidth(),
          }}
        >
          <FormControlLabel
            control={
              <Checkbox
                checked={getCheckedState(index)}
                onChange={() => onChange(index)}
                disabled={disabled}
                name={value}
                id={value}
                disableRipple
                sx={{ 
                  "&:hover": { backgroundColor: "transparent" },
                }}
              />
            }
            label={label}
            className={props.containerClassName}
            sx={{ marginRight: 0, alignItems: "center" }}
          />
        </Box>
      )}
    </Box>
  );
};

export default MCheckboxGroup;